import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import wsManager from '@/services/websocketManager';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Wyldecrest Auth Guard HOC
 * Wraps a component and checks for valid Wyldecrest token + view permission
 * 
 * OPTIMIZED FOR INSTANT LOAD:
 * - LOCAL-FIRST: Check token locally first (instant)
 * - Server verification happens in background (non-blocking)
 * - Prevents redirect loops
 * - Multi-tab awareness via storage events
 */
export function withWyldecrestAuth(WrappedComponent, requiredView) {
  return function AuthGuardedComponent(props) {
    const navigate = useNavigate();
    const location = useLocation();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState(null);

    // LOCAL-FIRST: Instantly check token validity without server round-trip
    const checkTokenLocally = useCallback(() => {
      const token = localStorage.getItem('wyldecrest_token');
      const userStr = localStorage.getItem('wyldecrest_user');
      const lhsToken = localStorage.getItem('token');

      // LHS admin token - instant allow
      if (lhsToken) {
        return { valid: true, user: null, token: lhsToken, isLhs: true };
      }

      // No wyldecrest token
      if (!token || !userStr) {
        return { valid: false, reason: 'no_token' };
      }

      // Decode JWT locally to check expiry
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          return { valid: false, reason: 'invalid_format' };
        }

        const payload = JSON.parse(atob(tokenParts[1]));
        
        // Check expiry
        if (!payload.exp || payload.exp * 1000 < Date.now()) {
          return { valid: false, reason: 'expired' };
        }

        // Parse user data
        const userData = JSON.parse(userStr);
        
        // Check view permissions
        const allowedViews = userData.allowed_views || [];
        if (requiredView && !allowedViews.includes(requiredView)) {
          const redirectView = allowedViews[0];
          return { valid: false, reason: 'no_permission', redirectView };
        }

        return { valid: true, user: userData, token };
      } catch (e) {
        return { valid: false, reason: 'parse_error' };
      }
    }, [requiredView]);

    // Run instant local check on mount
    useEffect(() => {
      const result = checkTokenLocally();

      if (result.valid) {
        setUser(result.user);
        setIsAuthenticated(true);
        setIsLoading(false);

        // Initialize WebSocket manager for real-time status indicator
        wsManager.initialize({
          token: result.token,
          userId: result.user?.email || 'anonymous'
        });

        // Background server verification (non-blocking)
        if (!result.isLhs && result.token) {
          axios.get(`${API}/api/wyldecrest-auth/verify`, {
            headers: { Authorization: `Bearer ${result.token}` },
            timeout: 5000
          }).then(res => {
            if (!res?.data?.valid) {
              // Token invalidated server-side - clear and redirect
              localStorage.removeItem('wyldecrest_token');
              localStorage.removeItem('wyldecrest_user');
              setIsAuthenticated(false);
              wsManager.disconnect();
              navigate('/admin/login', { replace: true });
            }
          }).catch(() => {
            // Network error - keep user logged in (token is locally valid)
          });
        }
      } else {
        // Token invalid locally - redirect immediately
        if (result.redirectView) {
          navigate(`/phs/${result.redirectView}`, { replace: true });
        } else {
          // Clear any stale tokens
          localStorage.removeItem('wyldecrest_token');
          localStorage.removeItem('wyldecrest_user');
          navigate('/admin/login', { state: { from: location.pathname }, replace: true });
        }
        setIsLoading(false);
      }
    }, [checkTokenLocally, navigate, location.pathname]);

    // Listen for storage changes (multi-tab logout sync)
    useEffect(() => {
      const handleStorageChange = (e) => {
        if (e.key === 'wyldecrest_token' && !e.newValue) {
          // Token was removed in another tab - log out here too
          setIsAuthenticated(false);
          setUser(null);
          navigate('/admin/login', { replace: true });
        }
      };
      
      window.addEventListener('storage', handleStorageChange);
      return () => window.removeEventListener('storage', handleStorageChange);
    }, [navigate]);

    // Instant render - no loading spinner for valid tokens
    if (isLoading) {
      // Only show for < 100ms max (local check is instant)
      return null;
    }

    if (!isAuthenticated) {
      return null;
    }

    return <WrappedComponent {...props} wyldecrestUser={user} />;
  };
}

/**
 * Hook to get current Wyldecrest user
 */
export function useWyldecrestUser() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userStr = localStorage.getItem('wyldecrest_user');
    if (userStr) {
      try {
        setUser(JSON.parse(userStr));
      } catch (e) {
        setUser(null);
      }
    }
  }, []);

  return user;
}

/**
 * Check if user has a specific permission
 */
export function useHasPermission(permission) {
  const user = useWyldecrestUser();
  if (!user) return true; // LHS admin has all permissions
  return user.permissions?.includes(permission) || false;
}

export default withWyldecrestAuth;
